import React, { useState, useEffect } from 'react';
import { X, Plus, Tag as TagIcon } from 'lucide-react';
import { InkQuoteApi } from '../../services/inkquoteApi';

const TagManager = ({ quoteId, currentTags = [], isOpen, onClose, onTagsUpdated }) => {
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([...currentTags]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadAllTags();
    }
  }, [isOpen]);

  const loadAllTags = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const tags = await InkQuoteApi.getAllTags();
      setAllTags(tags);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load tags:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleAddNewTag = () => {
    const trimmedTag = searchQuery.trim().toLowerCase();
    if (!trimmedTag) return;

    if (!selectedTags.includes(trimmedTag)) {
      setSelectedTags([...selectedTags, trimmedTag]);
    }
    if (!allTags.includes(trimmedTag)) {
      setAllTags([...allTags, trimmedTag].sort());
    }
    setSearchQuery('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // If exact match exists in filtered tags, select it
      const exactMatch = filteredAvailableTags.find(tag => tag.toLowerCase() === searchQuery.toLowerCase());
      if (exactMatch) {
        handleToggleTag(exactMatch);
        setSearchQuery('');
      } else {
        // Otherwise create new tag
        handleAddNewTag();
      }
    }
  };

  // Filter available tags based on search query
  const filteredAvailableTags = allTags.filter(tag =>
    !selectedTags.includes(tag) &&
    tag.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if search query would create a new tag
  const isNewTag = searchQuery.trim() &&
    !allTags.some(tag => tag.toLowerCase() === searchQuery.toLowerCase());

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await InkQuoteApi.updateQuoteTags(quoteId, selectedTags);
      onTagsUpdated(selectedTags);
      onClose();
    } catch (err) {
      setError(err.message);
      console.error('Failed to save tags:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <TagIcon size={20} />
            Manage Tags
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 p-3 rounded">
              {error}
            </div>
          )}

          {/* Search / Add Tag */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Search Tags
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Search or create new tag..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {isNewTag && (
                <button
                  onClick={handleAddNewTag}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2 whitespace-nowrap"
                >
                  <Plus size={16} />
                  Create "{searchQuery}"
                </button>
              )}
            </div>
          </div>

          {/* All Tags - Selected shown first, then available */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {selectedTags.length > 0 ? `Tags (${selectedTags.length} selected)` : 'Tags'}
            </label>
            {isLoading ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                Loading tags...
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-900/20 rounded border border-gray-200 dark:border-gray-700 min-h-[100px] max-h-[400px] overflow-y-auto">
                {/* Selected tags first - highlighted in blue */}
                {selectedTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleToggleTag(tag)}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium
                      bg-blue-500 text-white hover:bg-blue-600 transition-colors max-w-[200px] ring-2 ring-blue-300"
                    title={`Click to remove: ${tag}`}
                  >
                    <span className="truncate font-semibold">{tag}</span>
                    <X size={14} className="ml-1.5 flex-shrink-0" />
                  </button>
                ))}

                {/* Available tags */}
                {filteredAvailableTags.length === 0 && selectedTags.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                    {searchQuery ? 'No tags match your search' : 'No tags available'}
                  </div>
                ) : (
                  filteredAvailableTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => handleToggleTag(tag)}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                        bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200
                        hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300
                        transition-colors max-w-[200px]"
                      title={`Click to select: ${tag}`}
                    >
                      <Plus size={14} className="mr-1 flex-shrink-0" />
                      <span className="truncate">{tag}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Saving...
              </>
            ) : (
              'Save Tags'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagManager;
